import { useEffect, useMemo, useRef, useState } from 'react';
import {
  useAtomValue as useStateSegmentValue,
  useSetAtom as useSetStateSegment,
} from 'jotai';
import { RiExternalLinkLine, RiFileForbidLine } from '@remixicon/react';
import BrandsInfoTable from '../../BrandsInfoTable';
import styles from './ClaimsTab.styles.module.css';
import {
  knowledgeCenterStateSegment,
  Claim,
} from '../../../../store/markettingContentGeneration/stateSegments/brandsInformationStateSegment';
import Loading from '../../Loading';
import {
  fetchClaimDetails,
  getClaimDetails,
} from '../../../../store/markettingContentGeneration/actions/majorProcessActions';
import useInfiniteScroll from '../../../../hooks/infiniteScroll/useInfiniteScroll';
import { translate } from '../../../../localization';
import { GLOBAL } from '../../../../constants/common';
import BrandsMessage from '../BrandsMessage';
import Button from '../../../atoms/Button';
import Retry from '../Retry';
import useBrandMessageState from '../../../../hooks/brandInfoPage/useBrandMessageState';
import InfoLogo from '../InfoLogo';
import { TooltipPosition } from '../../InfoTooltip';
import { useWindowWidth } from '../../../../hooks/useWindowWidth';

interface ClaimsTabProps {
  isNonOnboardedBrand?: boolean;
}

const ClaimsTab: React.FC<ClaimsTabProps> = ({
  isNonOnboardedBrand = false,
}) => {
  const windowWidth = useWindowWidth();

  const { claimDetails, apiPayloadUsed } = useStateSegmentValue(
    knowledgeCenterStateSegment
  );

  const setBrandInformationSegment = useSetStateSegment(
    knowledgeCenterStateSegment
  );

  const {
    data: claimsData,
    isLoading: isPageLevelLoading,
    errorMessage: pageLevelErrorMessage,
  } = claimDetails;

  const loadingRef = useRef<HTMLDivElement>(null);

  const [tabLevelErrorCount, setTabLevelErrorCount] = useState(0);
  const [paginationLevelErrorCount, setPaginationLevelErrorCount] = useState(0);

  const {
    state: brandMessageConfig,
    updateScenarioNoData,
    updateScenarioTabLevelError,
    updateScenarioTabLevelApiDown,
    updateScenarioPaginationLevelError,
    updateScenarioPaginationLevelApiDown,
  } = useBrandMessageState();

  const columnsConfiguration = useMemo(() => {
    const baseColumns = [
      {
        title: translate('rank_column_header'),
        sortable: true,
        showInfoIcon: true,
        infoText: translate('rank_column_tooltip'),
        value: 'score',
        tooltipPosition: TooltipPosition.BOTTOM_RIGHT,
      },
      {
        title: translate('claim_description_column_header'),
        sortable: false,
        showInfoIcon: true,
        infoText: translate('claim_description_tooltip'),
        value: 'claimDescription',
        tooltipPosition: TooltipPosition.BOTTOM_RIGHT,
      },
      {
        title: translate('enriched_description_column_header'),
        sortable: false,
        showInfoIcon: true,
        infoText: translate('enriched_description_tooltip'),
        value: 'enrichedDescription',
      },
    ];

    const archTypeColumn = {
      title: translate('archtype_column_header'),
      sortable: false,
      showInfoIcon: true,
      infoText: translate('archtype_column_tooltip'),
      value: 'archType',
    };

    const endColumns = [
      {
        title: translate('primary_message_column_header'),
        sortable: true,
        showInfoIcon: true,
        infoText: translate('primary_message_tooltip'),
        value: 'primaryMessage',
      },
      {
        title: translate('secondary_column_header'),
        sortable: true,
        showInfoIcon: true,
        infoText: translate('secondary_column_tooltip'),
        tooltipPosition: TooltipPosition.BOTTOM_RIGHT,
        value: 'secondaryMessage',
      },
      {
        title: translate('references_column_header'),
        sortable: false,
        showInfoIcon: true,
        infoText: translate('references_column_tooltip'),
        tooltipPosition: TooltipPosition.BOTTOM_LEFT,
        value: 'references',
      },
    ];

    // Only show archtype column if country is 'global'
    if (apiPayloadUsed.country?.toLowerCase() === GLOBAL.toLowerCase()) {
      return [...baseColumns, archTypeColumn, ...endColumns];
    }

    return [...baseColumns, ...endColumns];
  }, [apiPayloadUsed.country]);

  const gridColStyle = useMemo(() => {
    const isGlobal =
      apiPayloadUsed.country?.toLowerCase() === GLOBAL.toLowerCase();

    if (isGlobal) {
      // With archetype column - 7 columns total
      if (windowWidth <= 1080) {
        // Mobile: Give more space to description columns, less to others
        return '10% 15% 15% 15% 15% 15% 15%';
      }
      if (windowWidth < 1280) {
        // Tablet: Balanced distribution
        return '6% 20% 20% 10% 12% 12% 20%';
      }
      // Desktop: Original layout
      return '8% 20% 20% 8% 12% 12% 20%';
    }

    // Without archetype column - 6 columns total
    if (windowWidth < 1080) {
      // Mobile: Prioritize description columns
      return '8% 22% 22% 13% 13% 22%';
    }
    if (windowWidth < 1280) {
      // Tablet: Balanced distribution
      return '8% 22% 22% 13% 13% 22%';
    }
    // Desktop: Original layout
    return '8% 22% 22% 13% 13% 22%';
  }, [apiPayloadUsed.country, windowWidth]);

  const tableData = useMemo(() => {
    return claimsData.claims.map(
      ({
        id,
        score,
        text: claimDescription,
        revisedText: enrichedDescription,
        primaryMessage,
        secondaryTopics,
        references,
        referenceLink,
        archetype,
      }) => {
        const claimDescriptionWithLink = (
          <p className={styles['claims-tab__description-text']}>
            {claimDescription}
            {referenceLink && (
              <a
                href={referenceLink}
                target='_blank'
                rel='noopener noreferrer'
                style={{ verticalAlign: 'middle' }}
              >
                <RiExternalLinkLine color='#0000C9' size={20} />
              </a>
            )}
          </p>
        );

        const baseData = {
          id,
          score,
          claimDescription: claimDescriptionWithLink,
          enrichedDescription,
          primaryMessage,
          secondaryMessage: secondaryTopics.join(', '),
          references: references.join('\n'),
        };

        // Only include archType if country is 'global'
        if (apiPayloadUsed.country?.toLowerCase() === GLOBAL.toLowerCase()) {
          return {
            ...baseData,
            archType: archetype,
          };
        }

        return baseData;
      }
    );
  }, [claimsData.claims, apiPayloadUsed.country]);

  const fetchData = async (page: number): Promise<Claim[]> => {
    const payload = {
      ...apiPayloadUsed,
      claimsPage: page,
      claimsPageSize: 20,
    };

    const { success, data: claimsResponseData } = await getClaimDetails({
      ...payload,
    });

    if (!success) {
      throw new Error('Manually thrown error!');
    }

    // update pagination
    setBrandInformationSegment(prev => {
      return {
        ...prev,
        claimDetails: {
          ...prev.claimDetails,
          data: {
            ...prev.claimDetails.data,
            claimsPagination: {
              ...prev.claimDetails.data.claimsPagination,
              ...claimsResponseData?.pagination,
            },
          },
        },
      };
    });

    return claimsResponseData?.claimDetails ?? [];
  };

  const {
    hasError: hasPaginationError,
    hasMore,
    resetHasMore: resetPaginationHasMore,
    resetError: resetPaginationError,
    resetConfigs: resetPaginationConfigs,
    setHasMoreFalse: setHasMorePaginationFalse,
  } = useInfiniteScroll<Claim>({
    fetchData,
    loadingElementRef: loadingRef as React.RefObject<HTMLElement>,
    initialPage: claimsData.claimsPagination.nextPage ?? 2,
    onSuccess: updatedClaimsData => {
      setBrandInformationSegment(prev => {
        return {
          ...prev,
          claimDetails: {
            ...prev.claimDetails,
            data: {
              ...prev.claimDetails.data,
              claims: [...prev.claimDetails.data.claims, ...updatedClaimsData],
            },
          },
        };
      });

      // reset the pagination level error count to 0 on successfull pagination  retry
      if (paginationLevelErrorCount > 0) {
        setPaginationLevelErrorCount(0);
      }
    },
    onError: () => {
      // increment the count of the pagination error count
      setPaginationLevelErrorCount(prev => prev + 1);
    },
  });

  const handleTabLevelRetry = async () => {
    const success = await fetchClaimDetails(apiPayloadUsed);
    if (success) {
      // if retry was success than reset the tab level error count
      setTabLevelErrorCount(0);
    } else {
      // increment the tab level error count on failure
      setTabLevelErrorCount(prev => prev + 1);
    }
  };

  const handlePaginationLevelRetry = () => {
    // reset the has more and error it will automatically fetch the next data because loader will be visible again
    resetPaginationHasMore();
    resetPaginationError();
  };

  useEffect(() => {
    if (
      !isPageLevelLoading &&
      pageLevelErrorMessage.length === 0 &&
      claimsData.claims.length === 0
    ) {
      updateScenarioNoData({
        logo: <RiFileForbidLine size={64} color='#A1A1AA' />,
        cta: null,
        title: translate('claims_no_data_title'),
        message: translate('claims_no_data_message'),
      });
      // if there is no data inside the claims than this useEffect will handle it
    }
  }, [isPageLevelLoading, claimsData.claims.length]);

  useEffect(() => {
    updateScenarioTabLevelError(
      <InfoLogo />,
      <Button
        type='button'
        size='m'
        variant='outline'
        colorScheme='#0000C9'
        onClick={handleTabLevelRetry}
      >
        <Retry />
      </Button>
    );
    // if there is any page level error that will be handled by this useEffect
  }, [pageLevelErrorMessage.length > 0]);

  useEffect(() => {
    if (
      !isPageLevelLoading &&
      pageLevelErrorMessage.length > 0 &&
      tabLevelErrorCount > 3
    ) {
      updateScenarioTabLevelApiDown(<InfoLogo />);
      // if tabLevelErrorCount is greater than 3 than we need to update the brand Message config
    }
  }, [tabLevelErrorCount]);

  useEffect(() => {
    // check the pagination level error count greater than 3
    if (paginationLevelErrorCount > 3) {
      // set the message for system down
      updateScenarioPaginationLevelApiDown();
    } else {
      // gives user to retry
      updateScenarioPaginationLevelError(
        null,
        <Button
          type='button'
          size='m'
          variant='outline'
          colorScheme='#0000C9'
          onClick={handlePaginationLevelRetry}
        >
          <Retry />
        </Button>
      );
    }
  }, [paginationLevelErrorCount]);

  useEffect(() => {
    if (isPageLevelLoading) {
      // if the pagelevel loading means there is a new request in than case we need to reset  pagination conFig
      resetPaginationConfigs();
    }
  }, [isPageLevelLoading]);

  useEffect(() => {
    if (!isPageLevelLoading && !claimsData.claimsPagination.nextPage) {
      // we need to stop showing pagination loader when there is no more pages
      setHasMorePaginationFalse();
    }
  }, [claimsData]);

  // Early return for non-onboarded brands
  if (isNonOnboardedBrand) {
    return (
      <div className={styles['claim-details__loading']}>
        <BrandsMessage
          logo={<RiFileForbidLine size={64} color='#A1A1AA' />}
          title={translate('claims_no_data_title')}
          message={translate('claims_no_data_message')}
          cta={null}
        />
      </div>
    );
  }

  if (isPageLevelLoading) {
    return (
      <div className={styles['claim-details__loading']}>
        <Loading />
      </div>
    );
  }

  if (
    !isPageLevelLoading &&
    (claimsData.claims.length === 0 || pageLevelErrorMessage.length > 0)
  ) {
    const { cta, logo, message, title } = brandMessageConfig;

    return (
      <div className={styles['claim-details__loading']}>
        <BrandsMessage logo={logo} title={title} message={message} cta={cta} />
      </div>
    );
  }

  return (
    <div>
      <BrandsInfoTable
        columnsConfiguration={columnsConfiguration}
        tableData={tableData}
        gridColumnsStyle={gridColStyle}
      />

      {hasMore && !hasPaginationError && (
        <div
          ref={loadingRef}
          className={styles['claim-details__pagination-loading']}
        >
          <Loading />
        </div>
      )}

      {hasPaginationError && (
        <div className={styles['claim-details__pagination-loading']}>
          <BrandsMessage
            logo={brandMessageConfig.logo}
            title={brandMessageConfig.title}
            message={brandMessageConfig.message}
            cta={brandMessageConfig.cta}
          />
        </div>
      )}
    </div>
  );
};

export default ClaimsTab;
